import type { CardRecord } from "@/lib/types";

/** Normalize for loose matching: "OGN 195" / "ogn-195" / "OGN195" → "ogn195" */
function compact(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, "");
}

/**
 * Match a card against a free-text query.
 * Supports name, set code + collector number (e.g. "OGN 195"), number alone ("195"),
 * and riftbound id fragments ("ogn-195").
 */
export function cardMatchesQuery(card: CardRecord, rawQuery: string): boolean {
  const q = rawQuery.trim().toLowerCase();
  if (!q) return true;

  const setId = (card.setId ?? "").toLowerCase();
  const number =
    card.collectorNumber == null ? "" : String(card.collectorNumber);
  const riftboundId = (card.riftboundId ?? "").toLowerCase();
  const setNumberLabel = setId && number ? `${setId} ${number}` : "";
  const setNumberCompact = setId && number ? `${setId}${number}` : "";

  const haystack = [
    card.name,
    card.cleanName,
    card.text,
    card.type,
    card.rarity,
    card.setId,
    card.setLabel,
    number,
    setNumberLabel,
    riftboundId,
    ...card.domains,
    ...card.tags,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  if (haystack.includes(q)) return true;

  const qCompact = compact(q);
  if (!qCompact) return true;

  if (setNumberCompact && setNumberCompact.includes(qCompact)) return true;
  if (riftboundId && compact(riftboundId).includes(qCompact)) return true;

  // Pure number → match collector number (exact or as string contains)
  if (/^\d+$/.test(qCompact)) {
    if (number && (number === qCompact || number.includes(qCompact))) return true;
  }

  // "OGN 195" / "ogn-195" style: letters then digits
  const setNum = qCompact.match(/^([a-z]+)(\d+)$/);
  if (setNum) {
    const [, setPart, numPart] = setNum;
    if (setId === setPart && number === numPart) return true;
    if (setId.startsWith(setPart) && number.startsWith(numPart)) return true;
  }

  return compact(haystack).includes(qCompact);
}
